import type { InteractionTopicInput } from "../queries";
import { OTHER_TOPIC } from "../queries";

export type TopicValidationErrors = {
  topicErrors: Record<number, string | undefined>;
  subtopicErrors: Record<number, string | undefined>;
  formError?: string;
};

export const OTHER_TOPIC_WARNING = `"${OTHER_TOPIC}" is always shown — remove this row.`;
export const OTHER_SUBTOPIC_WARNING = `"${OTHER_TOPIC}" is added automatically — remove this line.`;
export const MIN_TOPICS_ERROR = `Add at least one topic — "${OTHER_TOPIC}" alone is not enough.`;

export function validateInteractionTopics(topics: InteractionTopicInput[]): TopicValidationErrors {
  const topicErrors: Record<number, string | undefined> = {};
  const subtopicErrors: Record<number, string | undefined> = {};

  const seenTopics = new Map<string, number>();
  topics.forEach((topic, topicIndex) => {
    const name = topic.name.trim();
    if (!name) {
      topicErrors[topicIndex] = "Topic name is required.";
    } else if (name.toLowerCase() === OTHER_TOPIC.toLowerCase() && !isTopicLocked(topics, topicIndex)) {
      // A row renamed to Other would silently dedupe away on save — warn to
      // remove it instead of showing a confusing duplicate error.
      topicErrors[topicIndex] = OTHER_TOPIC_WARNING;
    } else {
      const key = name.toLowerCase();
      if (seenTopics.has(key)) {
        topicErrors[topicIndex] = "Duplicate topic.";
        const firstIndex = seenTopics.get(key);
        if (firstIndex !== undefined && !topicErrors[firstIndex]) {
          topicErrors[firstIndex] = "Duplicate topic.";
        }
      } else {
        seenTopics.set(key, topicIndex);
      }
    }

    // Subtopics edit as one textarea (one per line). Blank lines are ignored
    // (and stripped at save); only the first duplicate is reported. The
    // canonical trailing Other is a save-time concern (the backend appends
    // it), so a typed Other warns here instead of duplicating.
    const seenSubtopics = new Set<string>();
    for (const subtopic of topic.subtopics) {
      const subName = subtopic.trim();
      if (!subName) continue;
      const lower = subName.toLowerCase();
      if (lower === OTHER_TOPIC.toLowerCase()) {
        subtopicErrors[topicIndex] = OTHER_SUBTOPIC_WARNING;
        break;
      }
      if (seenSubtopics.has(lower)) {
        subtopicErrors[topicIndex] = `Duplicate subtopic "${subName}".`;
        break;
      }
      seenSubtopics.add(lower);
    }
  });

  const errors: TopicValidationErrors = { topicErrors, subtopicErrors };
  const realTopics = topics.filter(
    (topic, topicIndex) => !isTopicLocked(topics, topicIndex) && topic.name.trim().length > 0
  );
  if (realTopics.length === 0) {
    errors.formError = MIN_TOPICS_ERROR;
  }

  return errors;
}

export function hasValidationErrors(errors: TopicValidationErrors): boolean {
  return (
    Boolean(errors.formError) ||
    Object.values(errors.topicErrors).some(Boolean) ||
    Object.values(errors.subtopicErrors).some(Boolean)
  );
}

export function topicsEqual(a: InteractionTopicInput[], b: InteractionTopicInput[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((topic, index) => {
    const other = b[index];
    if (
      topic.name.trim() !== other.name.trim() ||
      (topic.code ?? null) !== (other.code ?? null) ||
      (topic.defaultPrivate ?? null) !== (other.defaultPrivate ?? null)
    ) {
      return false;
    }
    const aSubs = topic.subtopics.map((line) => line.trim()).filter((line) => line.length > 0);
    const bSubs = other.subtopics.map((line) => line.trim()).filter((line) => line.length > 0);
    if (aSubs.length !== bSubs.length) return false;
    return aSubs.every((sub, subIndex) => sub === bSubs[subIndex]);
  });
}

// Merges a server echo into local edit state without dropping in-progress
// whitespace: when the echo matches local content modulo trimming and blank
// lines, local raw text wins (so a blank line being typed survives the echo
// that strips it) while server-owned metadata is still absorbed (otherwise a
// derived flag would read dirty forever and resave in a loop). Anything
// structurally different takes the echo whole.
export function mergeEchoTopics(
  localTopics: InteractionTopicInput[],
  echoTopics: InteractionTopicInput[]
): InteractionTopicInput[] {
  if (localTopics.length !== echoTopics.length) return echoTopics;
  const allMatch = localTopics.every((topic, index) => topicTextEqual(topic, echoTopics[index]));
  if (!allMatch) return echoTopics;
  return echoTopics.map((echo, index) => ({
    ...echo,
    name: localTopics[index].name,
    subtopics: localTopics[index].subtopics
  }));
}

function topicTextEqual(a: InteractionTopicInput, b: InteractionTopicInput): boolean {
  if (a.name.trim() !== b.name.trim()) return false;
  const aSubs = a.subtopics.map((line) => line.trim()).filter((line) => line.length > 0);
  const bSubs = b.subtopics.map((line) => line.trim()).filter((line) => line.length > 0);
  return aSubs.length === bSubs.length && aSubs.every((sub, subIndex) => sub === bSubs[subIndex]);
}

export function isOtherTopicName(name: string): boolean {
  return name.trim() === OTHER_TOPIC;
}

// The canonical Other row is always last (the editor inserts before it and the
// backend moves it last). Locking only the last Other keeps a row renamed to
// Other editable so the author can fix it instead of freezing.
export function isTopicLocked(topics: InteractionTopicInput[], topicIndex: number): boolean {
  return topicIndex === topics.length - 1 && isOtherTopicName(topics[topicIndex]?.name ?? "");
}
